/**
 * AuthProvider — drží user + accessToken v paměti, řeší login/logout/refresh
 * proti tickets-portal API.
 *
 * Token persistence: pouze in-memory (po reloadu se ztratí). Po mount se
 * zavolá `/api/auth/refresh`, který se pokusí dotáhnout user info z httpOnly
 * cookie. Pokud cookie neexistuje (typicky cross-origin nebo HTTP-only blok),
 * uživatel uvidí login.
 *
 * Periodický refresh každých 10 minut (pokud je přihlášen), aby accessToken
 * nezprotekl.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  loginRequest,
  logoutRequest,
  refreshRequest,
  type AuthUser,
} from "../api/auth.js";

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 min

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Zabráníme dvojímu init v React.StrictMode
  const initRef = useRef(false);

  // Initial refresh — pokud je platná cookie, dostaneme nazpět user.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    let cancelled = false;
    void (async () => {
      const data = await refreshRequest();
      if (cancelled) return;
      if (data.user && data.accessToken) {
        setUser(data.user);
        setAccessToken(data.accessToken);
      }
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Periodický refresh, jen pokud je uživatel přihlášen.
  // Pokud je invoice frontend cross-origin proti tickets-portal a httpOnly
  // refresh cookie nedorazí (typicky HTTP klient → HTTPS server, SameSite),
  // refresh selže opakovaně. Nesmíme proto shazovat session při každém
  // failu — uživatele by to vyhazovalo náhodně každých 10 minut.
  // Místo toho jen logujeme a držíme aktuální token, dokud explicit logout
  // nebo dokud server access token přijímá.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      void (async () => {
        const data = await refreshRequest();
        if (data.user && data.accessToken) {
          setUser(data.user);
          setAccessToken(data.accessToken);
        } else {
          // eslint-disable-next-line no-console
          console.warn("[auth] periodic refresh failed:", data.error);
        }
      })();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        const data = await loginRequest(email, password);
        setUser(data.user);
        setAccessToken(data.accessToken);
        return { ok: true };
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Přihlášení selhalo";
        return { ok: false, error: msg };
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
    setAccessToken(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      isLoading,
      isAuthenticated: !!user && !isLoading,
      login,
      logout,
    }),
    [user, accessToken, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
